import type { Device, LiveSnapshot } from "@fts/core";
import { useEffect, useState } from "react";
import type { DataProvider, LiveFeedStatus } from "../data";
import { mergeFullLoad, mergeSnapshot } from "../lib/live";

/** Full reload interval: a safety net for missed Realtime messages and device online flags. */
export const LIVE_REFRESH_MS = 60_000;
/** Re-classify this often so time-based states (offline, faded position, alarm window) age correctly. */
export const LIVE_CLOCK_MS = 15_000;

export type LiveData = {
  snapshots: Map<string, LiveSnapshot>;
  /** Fresh device list (online flags) or null until the first refresh; fall back to the session's devices. */
  devices: Device[] | null;
  status: LiveFeedStatus;
  loaded: boolean;
  error: boolean;
  now: Date;
};

/** Latest states of the tenant's vehicles, kept current by Realtime plus a periodic full refresh. */
export function useLive(provider: DataProvider, tenantId: string): LiveData {
  const [snapshots, setSnapshots] = useState<Map<string, LiveSnapshot>>(() => new Map());
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [status, setStatus] = useState<LiveFeedStatus>("connecting");
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    // Per-run flag: late answers of a previous tenant (or an unsubscribed channel) are ignored.
    let cancelled = false;
    setSnapshots(new Map());
    setDevices(null);
    setLoaded(false);
    setError(false);
    setStatus("connecting");

    const load = async () => {
      try {
        const [rows, devs] = await Promise.all([provider.listLive(tenantId), provider.listDevices(tenantId)]);
        if (cancelled) return;
        // Devices that left the tenant disappear; rows Realtime delivered meanwhile are kept if newer.
        setSnapshots((m) => mergeFullLoad(m, rows));
        setDevices(devs);
        setLoaded(true);
        setError(false);
      } catch {
        if (!cancelled) {
          setError(true);
          setLoaded(true);
        }
      }
      if (!cancelled) setNow(new Date());
    };

    void load();
    const unsubscribe = provider.subscribeLive(
      (s) => {
        if (cancelled) return;
        setSnapshots((m) => mergeSnapshot(m, s));
        setNow(new Date());
      },
      (st) => {
        if (cancelled) return;
        setStatus(st);
        // Catch up on whatever happened while the feed was down.
        if (st === "live") void load();
      },
    );
    const refresh = setInterval(load, LIVE_REFRESH_MS);
    const clock = setInterval(() => !cancelled && setNow(new Date()), LIVE_CLOCK_MS);
    return () => {
      cancelled = true;
      unsubscribe();
      clearInterval(refresh);
      clearInterval(clock);
    };
  }, [provider, tenantId]);

  return { snapshots, devices, status, loaded, error, now };
}
